import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';
import type { CbctProcessingPhase } from '../../files/useCbctProcessing';

type StepStatus = 'done' | 'active' | 'failed' | 'upcoming';

interface Step {
    label: string;
    status: StepStatus;
    subLabel?: string;
}

const STEPS_BY_PHASE: Partial<Record<CbctProcessingPhase, Step[]>> = {
    queued: [
        { label: 'Job created', status: 'done' },
        { label: 'Processing', status: 'active', subLabel: 'Queued…' },
        { label: 'Ready to view', status: 'upcoming' }
    ],
    running: [
        { label: 'Job created', status: 'done' },
        { label: 'Processing', status: 'active', subLabel: 'Processing…' },
        { label: 'Ready to view', status: 'upcoming' }
    ],
    succeeded: [
        { label: 'Job created', status: 'done' },
        { label: 'Processing', status: 'done' },
        { label: 'Ready to view', status: 'active', subLabel: 'Loading scan…' }
    ],
    failed: [
        { label: 'Job created', status: 'done' },
        { label: 'Processing', status: 'failed', subLabel: 'Failed' },
        { label: 'Ready to view', status: 'upcoming' }
    ]
};

const CIRCLE_CLASSES: Record<StepStatus, string> = {
    done: 'bg-gray-800 border-gray-800',
    active: 'bg-white border-gray-800',
    failed: 'bg-red-50 border-red-300',
    upcoming: 'bg-white border-gray-300'
};

const LABEL_CLASSES: Record<StepStatus, string> = {
    done: 'text-gray-700',
    active: 'text-gray-700',
    failed: 'text-red-500',
    upcoming: 'text-gray-400'
};

const StepCircle: React.FC<{ status: StepStatus }> = ({ status }) => (
    <div
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200 ${CIRCLE_CLASSES[status]}`}
    >
        {status === 'done' && <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
        {status === 'active' && (
            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
        )}
        {status === 'failed' && <XMarkIcon className="h-3.5 w-3.5 text-red-500" strokeWidth={3} />}
    </div>
);

export interface CbctProcessingStepperProps {
    phase: CbctProcessingPhase;
}

const CbctProcessingStepper: React.FC<CbctProcessingStepperProps> = ({ phase }) => {
    const steps = STEPS_BY_PHASE[phase];
    if (!steps) return null;

    return (
        <div className="flex w-full max-w-xs items-start justify-center">
            {steps.map((step, index) => (
                <React.Fragment key={step.label}>
                    {index > 0 && (
                        <div
                            className={`mt-[13px] h-0.5 flex-1 -mx-6 transition-colors duration-200 ${
                                steps[index - 1].status === 'done' ? 'bg-gray-800' : 'bg-gray-200'
                            }`}
                        />
                    )}
                    <div className="flex w-20 flex-col items-center">
                        <StepCircle status={step.status} />
                        <span
                            className={`mt-2 text-xs font-semibold transition-colors duration-200 ${LABEL_CLASSES[step.status]}`}
                        >
                            {step.label}
                        </span>
                        {step.subLabel && (
                            <span
                                className={`mt-0.5 text-xs ${
                                    step.status === 'failed' ? 'text-red-500' : 'text-gray-500'
                                }`}
                            >
                                {step.subLabel}
                            </span>
                        )}
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
};

export default CbctProcessingStepper;
